import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Home, Package } from "lucide-react";
import { format } from "date-fns";

interface Shipment {
  tracking_number?: string;
  label_url?: string;
  delivery_date?: string;
  status: string;
  easypost_tracking_url?: string;
}

interface ReturnShipmentCardProps {
  shipment: Shipment;
  onEmailLabel: () => void;
}

export function ReturnShipmentCard({ shipment, onEmailLabel }: ReturnShipmentCardProps) {
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      scheduled: { variant: "secondary" as const, label: "Scheduled" },
      in_transit: { variant: "default" as const, label: "In Transit" },
      completed: { variant: "outline" as const, label: "Delivered" },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.scheduled;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Home className="h-5 w-5" />
            Returning to You
          </span>
          {getStatusBadge(shipment.status)}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {shipment.tracking_number && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Tracking Number</p>
            <div className="flex items-center gap-2">
              <code className="text-sm bg-muted px-2 py-1 rounded flex-1">
                {shipment.tracking_number}
              </code>
              <Button
                size="sm"
                variant="outline"
                asChild
              >
                <a
                  href={shipment.easypost_tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Track
                </a>
              </Button>
            </div>
          </div>
        )}

        {shipment.delivery_date && (
          <div className="flex items-start gap-3">
            <Package className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Estimated Delivery</p>
              <p className="text-sm text-muted-foreground">
                {format(new Date(shipment.delivery_date), "EEEE, MMMM d, yyyy")}
              </p>
            </div>
          </div>
        )}

        {shipment.label_url && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" asChild>
              <a href={shipment.label_url} download target="_blank" rel="noopener noreferrer">
                Download Label
              </a>
            </Button>
            <Button size="sm" variant="outline" onClick={onEmailLabel}>
              Email Label
            </Button>
          </div>
        )}

        <div className="pt-3 border-t">
          <p className="text-xs text-muted-foreground">
            Your box is on its way back to you. You'll receive email updates about the delivery.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
